import React, {useState} from 'react';
import {Box, Typography} from '@mui/material';
import IndividualDashboardShell from '../components/IndividualDashboard.comp';
import CircularLoader from '../../../components/CircularLoader';
import NoDataAvailable from '../../../components/NoDataAvailable';
import {NAVY, PageHeader, ScoreRow, BenchmarkLegend} from '../components/widgets.comp';
import { useDashboard } from '../../../context/DashboardContext';
import { CONTEXT_BAND_COLORS } from '../constants';
import {SortToggle} from '../../DashboardCommon/SortToggle.comp';
import {sortItems} from '../../DashboardCommon/barSort';

const CONTEXT_SUPPORT_SECTION_CODE = 'S_4';
const CARD_SECTIONS = [CONTEXT_SUPPORT_SECTION_CODE];

const ContextSupportPage = () => {
    const { dashboardData, loading } = useDashboard({ card_sections: CARD_SECTIONS });
    const [sortMode, setSortMode] = useState('group');   // group order · highest first · lowest first (QA 9 Oct)

    const s4Section = dashboardData?.cards?.sections?.find(s => s.section_code === CONTEXT_SUPPORT_SECTION_CODE);

    if (loading) {
        return (
            <IndividualDashboardShell>
                <CircularLoader />
            </IndividualDashboardShell>
        );
    }

    if (!dashboardData || !s4Section) {
        return (
            <IndividualDashboardShell>
                <NoDataAvailable />
            </IndividualDashboardShell>
        );
    }

    const s4Cards = s4Section?.cards || [];

    const c1Card = s4Cards.find(c => c.card_type === 'TitleCard');
    const c2Card = s4Cards.find(c => c.card_type === 'BarChartBMCard');

    // Mockup titles (ID slide 80) when the card sends none.
    const title = c1Card?.title || "My Context & Support";
    const subtitle = c1Card?.footer || c1Card?.header || "What support conditions do I have?";

    const readinessItems = c2Card?.items || [];

    const getAreaLabel = (item, index) => {
        const match = item.sublabel?.match(/\d+/);
        const num = match ? match[0] : index + 1;
        return `Context & Support Area ${num}`;
    };
    // Each area keeps the label of its place in the card, whichever order it is shown in.
    const shownItems = sortItems(readinessItems.map((it, idx) => ({...it, area: getAreaLabel(it, idx)})), sortMode);

    return (
        <IndividualDashboardShell>
            <PageHeader
                title={title}
                subtitle={subtitle}
            />

            <Box sx={{border: '1px solid var(--brand-border, #E5E7EB)', borderRadius: '12px', backgroundColor: 'var(--brand-surface, #fff)', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', p: 3}}>
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 1.5}}>
                    <Typography sx={{fontSize: '16px', fontWeight: 700, color: NAVY}}>{c2Card?.title || "Context & Support Area Scores"}</Typography>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap'}}>
                        <BenchmarkLegend bandColorMap={CONTEXT_BAND_COLORS}/>
                        <SortToggle value={sortMode} onChange={setSortMode}/>
                    </Box>
                </Box>
                {shownItems.map((it) => (
                    <ScoreRow
                        key={it.code}
                        area={it.area}
                        label={it.label}
                        score={Math.trunc(Number(it.value))}
                        band={it.band}
                        marker={it.benchmark_values?.find(b => b.name === 'Class avg.')?.value}
                        labelWidth={{xs: 170, md: 260}}
                        bandColorMap={CONTEXT_BAND_COLORS}
                    />
                ))}
            </Box>
        </IndividualDashboardShell>
    );
};

export default ContextSupportPage;
